<template>
  <div class="container-fluid">
    <div class="page-title">
      <div class="row">
        <div class="col-6">
          <h3>Detalle de viaje</h3>
        </div>
        <div class="col-6 text-end">
          <button class="btn btn-outline-secondary" @click="$router.replace('/trips/consulta')">Regresar</button>
        </div>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
    </div>

    <div v-else-if="errorMsg" class="alert alert-danger">{{ errorMsg }}</div>

    <div v-else-if="trip" class="card">
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-6">
            <div class="p-3 border rounded">
              <h5 class="mb-1">Origen</h5>
              <div class="text-muted small">{{ trip.originAddress }}</div>
              <div class="small">{{ formatCoord(trip.originLatitude) }}, {{ formatCoord(trip.originLongitude) }}</div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="p-3 border rounded">
              <h5 class="mb-1">Destino</h5>
              <div class="text-muted small">{{ trip.destinationAddress }}</div>
              <div class="small">{{ formatCoord(trip.destinationLatitude) }}, {{ formatCoord(trip.destinationLongitude) }}</div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="p-3 border rounded h-100">
              <h5 class="mb-1">Fecha del viaje</h5>
              <div class="text-muted small">{{ formatDate(trip.tripDate) }}</div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="p-3 border rounded h-100">
              <h5 class="mb-1">Hora del viaje</h5>
              <div class="text-muted small">{{ formatTime(trip.tripTime) }}</div>
            </div>
          </div>
          <div class="col-12 d-flex justify-content-end gap-2 mt-3">
            <button class="btn btn-primary" :disabled="!canModifyTrip" @click="$router.push(`/trips/editar/${trip.id}`)">
              Editar datos del viaje
            </button>
            <button class="btn btn-danger" :disabled="!canModifyTrip || trip.status === 'CANCELADO'" @click="cancelTrip">
              Cancelar viaje
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="trip" class="card mt-3">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h5 class="mb-0">Solicitudes de paquetes pendientes</h5>
          <span class="badge bg-warning text-dark">{{ pendingPackages.length }}</span>
        </div>
        <div v-if="pendingLoading" class="text-muted">Cargando solicitudes...</div>
        <div v-else-if="pendingError" class="alert alert-danger py-2">{{ pendingError }}</div>
        <div v-else-if="pendingPackages.length === 0" class="text-muted">No hay solicitudes pendientes.</div>
        <div v-else class="table-responsive">
          <table class="table table-hover align-middle">
            <thead>
              <tr>
                <th>Remitente</th>
                <th>Destinatario</th>
                <th>Descripción</th>
                <th class="text-end">Valor</th>
                <th class="text-end">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="pkg in pendingPackages" :key="pkg.id">
                <td>{{ pkg.senderName }}</td>
                <td>
                  <div class="fw-semibold">{{ pkg.recipientName }}</div>
                  <div class="small text-muted">{{ pkg.recipientPhone || '—' }}</div>
                </td>
                <td class="text-break">{{ pkg.description }}</td>
                <td class="text-end fw-semibold">{{ formatMxn(pkg.offeredPriceMxn) }}</td>
                <td class="text-end text-nowrap">
                  <button type="button" class="btn btn-sm btn-outline-primary me-1" @click="openPackageDetail(pkg)">
                    <vue-feather type="eye" size="14" class="align-middle me-1"></vue-feather>
                    Ver
                  </button>
                  <button type="button" class="btn btn-sm btn-success me-1" :disabled="actionLoadingId === pkg.id" @click="accept(pkg)">
                    {{ actionLoadingId === pkg.id ? '...' : 'Aceptar' }}
                  </button>
                  <button type="button" class="btn btn-sm btn-outline-danger" :disabled="actionLoadingId === pkg.id" @click="reject(pkg)">
                    {{ actionLoadingId === pkg.id ? '...' : 'Rechazar' }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-if="trip" class="card mt-3">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h5 class="mb-0">Paquetes aceptados</h5>
          <span class="badge bg-success">{{ acceptedPackages.length }}</span>
        </div>
        <div v-if="decidedLoading" class="text-muted">Cargando...</div>
        <div v-else-if="decidedError" class="alert alert-danger py-2">{{ decidedError }}</div>
        <div v-else-if="acceptedPackages.length === 0" class="text-muted">No hay paquetes aceptados.</div>
        <div v-else class="table-responsive">
          <table class="table table-hover align-middle">
            <thead>
              <tr>
                <th>Remitente</th>
                <th>Destinatario</th>
                <th>Descripción</th>
                <th class="text-end">Valor</th>
                <th class="text-end">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="pkg in acceptedPackages" :key="pkg.id">
                <td>{{ pkg.senderName }}</td>
                <td>
                  <div class="fw-semibold">{{ pkg.recipientName }}</div>
                  <div class="small text-muted">{{ pkg.recipientPhone || '—' }}</div>
                </td>
                <td class="text-break">{{ pkg.description }}</td>
                <td class="text-end fw-semibold">{{ formatMxn(pkg.offeredPriceMxn) }}</td>
                <td class="text-end text-nowrap">
                  <button type="button" class="btn btn-sm btn-outline-primary" @click="openPackageDetail(pkg)">
                    <vue-feather type="eye" size="14" class="align-middle me-1"></vue-feather>
                    Ver
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-if="trip" class="card mt-3">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h5 class="mb-0">Paquetes rechazados</h5>
          <span class="badge bg-danger">{{ rejectedPackages.length }}</span>
        </div>
        <div v-if="decidedLoading" class="text-muted">Cargando...</div>
        <div v-else-if="decidedError" class="alert alert-danger py-2">{{ decidedError }}</div>
        <div v-else-if="rejectedPackages.length === 0" class="text-muted">No hay paquetes rechazados.</div>
        <div v-else class="table-responsive">
          <table class="table table-hover align-middle">
            <thead>
              <tr>
                <th>Remitente</th>
                <th>Destinatario</th>
                <th>Descripción</th>
                <th class="text-end">Valor</th>
                <th class="text-end">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="pkg in rejectedPackages" :key="pkg.id">
                <td>{{ pkg.senderName }}</td>
                <td>
                  <div class="fw-semibold">{{ pkg.recipientName }}</div>
                  <div class="small text-muted">{{ pkg.recipientPhone || '—' }}</div>
                </td>
                <td class="text-break">{{ pkg.description }}</td>
                <td class="text-end fw-semibold">{{ formatMxn(pkg.offeredPriceMxn) }}</td>
                <td class="text-end text-nowrap">
                  <button type="button" class="btn btn-sm btn-outline-primary" @click="openPackageDetail(pkg)">
                    <vue-feather type="eye" size="14" class="align-middle me-1"></vue-feather>
                    Ver
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <div v-else class="alert alert-warning">
      No se encontró información del viaje.
    </div>

    <teleport to="body">
      <div id="packageDetailModalTrip" ref="packageDetailModalRef" class="modal fade" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-lg modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header border-0 pb-0 align-items-start">
              <h5 class="modal-title">Detalle del paquete</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Cerrar"></button>
            </div>
            <div v-if="selectedPackage" class="modal-body pt-2">
              <div class="product-box row">
                <div class="product-img col-lg-5 mb-3 mb-lg-0">
                  <div class="card border-0 shadow-sm h-100">
                    <div class="card-body text-center p-4 d-flex flex-column justify-content-center align-items-center">
                      <div
                        class="rounded-3 d-inline-flex align-items-center justify-content-center mb-3"
                        style="width: 140px; height: 140px; background: linear-gradient(145deg, #ecf4ff 0%, #fff8e6 100%);"
                      >
                        <vue-feather type="package" size="56" class="text-primary"></vue-feather>
                      </div>
                      <div class="product-price mb-1">
                        <span class="fs-3 fw-bold text-primary">{{ formatMxn(selectedPackage.offeredPriceMxn) }}</span>
                      </div>
                      <div class="text-muted small mb-2">Valor a pagar (MXN)</div>
                      <span class="badge bg-info mt-3">{{ selectedPackage.status }}</span>
                    </div>
                  </div>
                </div>
                <div class="product-details col-lg-7">
                  <h6 class="f-w-600 mb-3">Información del envío</h6>
                  <p class="text-muted small mb-3">{{ selectedPackage.description || 'Sin descripción.' }}</p>
                  <div class="row g-3">
                    <div class="col-sm-6">
                      <label class="text-muted small mb-1 d-block">Remitente</label>
                      <div class="fw-semibold">{{ selectedPackage.senderName }}</div>
                    </div>
                    <div class="col-sm-6">
                      <label class="text-muted small mb-1 d-block">Destinatario</label>
                      <div class="fw-semibold">{{ selectedPackage.recipientName }}</div>
                      <div class="small text-muted">{{ selectedPackage.recipientPhone || '—' }}</div>
                    </div>
                    <div class="col-12">
                      <label class="text-muted small mb-1 d-block">Origen</label>
                      <div class="text-break">{{ selectedPackage.originAddress }}</div>
                    </div>
                    <div class="col-12">
                      <label class="text-muted small mb-1 d-block">Destino</label>
                      <div class="text-break">{{ selectedPackage.destinationAddress }}</div>
                    </div>
                    <div class="col-sm-6">
                      <label class="text-muted small mb-1 d-block">Peso y medidas</label>
                      <div>{{ selectedPackage.weightKg }} kg</div>
                      <div class="small text-muted">
                        {{ selectedPackage.lengthCm }} × {{ selectedPackage.widthCm }} × {{ selectedPackage.heightCm }} cm
                      </div>
                    </div>
                    <div class="col-sm-6">
                      <label class="text-muted small mb-1 d-block">Urgencia</label>
                      <div>{{ selectedPackage.urgency ? 'Sí' : 'No' }}</div>
                    </div>
                    <div v-if="selectedPackage.deliveryAddress" class="col-12">
                      <label class="text-muted small mb-1 d-block">Entrega personalizada</label>
                      <div class="text-break">{{ selectedPackage.deliveryAddress }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="modal-footer border-0 pt-0">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cerrar</button>
            </div>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
import { Modal } from 'bootstrap';
import Swal from 'sweetalert2';
import { tripsApi } from '@/api/trips';
import { packagesApi } from '@/api/packages';

export default {
  name: 'DetalleViaje',
  data() {
    return {
      loading: true,
      errorMsg: '',
      trip: null,
      pendingLoading: false,
      pendingError: '',
      pendingPackages: [],
      decidedLoading: false,
      decidedError: '',
      acceptedPackages: [],
      rejectedPackages: [],
      selectedPackage: null,
      actionLoadingId: '',
    };
  },
  computed: {
    currentRole() {
      return this.$store.getters['auth/role'];
    },
    currentUserId() {
      return this.$store.getters['auth/user']?.id;
    },
    canModifyTrip() {
      if (!this.trip) return false;
      if (this.currentRole === 'ADMINISTRADOR') return true;
      return this.currentRole === 'CONDUCTOR_VIAJERO' && this.trip.travelerId === this.currentUserId;
    },
  },
  mounted() {
    this.loadTrip();
    this.$nextTick(() => {
      const el = this.$refs.packageDetailModalRef;
      if (el) el.addEventListener('hidden.bs.modal', this.onModalHidden);
    });
  },
  beforeUnmount() {
    const el = this.$refs.packageDetailModalRef;
    if (el) el.removeEventListener('hidden.bs.modal', this.onModalHidden);
  },
  methods: {
    formatCoord(v) {
      return Number.isFinite(v) ? Number(v).toFixed(6) : '-';
    },
    formatDate(d) {
      if (!d) return '-';
      return new Date(d).toLocaleDateString('es-ES');
    },
    formatTime(d) {
      if (!d) return '-';
      return new Date(d).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    },
    async loadTrip() {
      this.loading = true;
      this.errorMsg = '';
      try {
        const { data } = await tripsApi.getById(this.$route.params.id);
        this.trip = data;
        await this.loadPendingPackages();
        await this.loadDecidedPackages();
      } catch (e) {
        this.errorMsg = e.response?.data?.message || 'No se pudo cargar el detalle del viaje.';
      } finally {
        this.loading = false;
      }
    },
    async loadPendingPackages() {
      if (!this.trip?.id) return;
      this.pendingLoading = true;
      this.pendingError = '';
      try {
        const { data } = await packagesApi.getAll({
          page: 1,
          limit: 100,
          tripId: this.trip.id,
          status: 'PUBLICADO',
        });
        this.pendingPackages = data?.items || [];
      } catch (e) {
        this.pendingError = e.response?.data?.message || 'No se pudieron cargar las solicitudes.';
        this.pendingPackages = [];
      } finally {
        this.pendingLoading = false;
      }
    },
    async loadDecidedPackages() {
      if (!this.trip?.id) return;
      this.decidedLoading = true;
      this.decidedError = '';
      try {
        const [accepted, rejected] = await Promise.all([
          packagesApi.getAll({ page: 1, limit: 100, tripId: this.trip.id, status: 'ACEPTADO' }),
          packagesApi.getAll({ page: 1, limit: 100, tripId: this.trip.id, status: 'RECHAZADO' }),
        ]);
        this.acceptedPackages = accepted?.data?.items || [];
        this.rejectedPackages = rejected?.data?.items || [];
      } catch (e) {
        this.decidedError = e.response?.data?.message || 'No se pudieron cargar los paquetes aceptados/rechazados.';
        this.acceptedPackages = [];
        this.rejectedPackages = [];
      } finally {
        this.decidedLoading = false;
      }
    },
    formatMxn(amount) {
      const n = Number(amount) || 0;
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2,
      }).format(n);
    },
    openPackageDetail(pkg) {
      this.selectedPackage = { ...pkg };
      this.$nextTick(() => {
        const el = this.$refs.packageDetailModalRef || document.getElementById('packageDetailModalTrip');
        if (!el) return;
        Modal.getOrCreateInstance(el).show();
      });
    },
    onModalHidden() {
      this.selectedPackage = null;
    },
    async accept(pkg) {
      const confirm = await Swal.fire({
        icon: 'question',
        title: 'Aceptar paquete',
        text: '¿Confirmas que transportarás este paquete?',
        showCancelButton: true,
        confirmButtonText: 'Aceptar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#198754',
      });
      if (!confirm.isConfirmed) return;
      this.actionLoadingId = pkg.id;
      try {
        await packagesApi.accept(pkg.id);
        await Swal.fire({ icon: 'success', title: 'Paquete aceptado', confirmButtonColor: '#0d6efd' });
        await this.loadPendingPackages();
        await this.loadDecidedPackages();
      } catch (e) {
        await Swal.fire({ icon: 'error', title: 'Error', text: e.response?.data?.message || 'No se pudo aceptar.' });
      } finally {
        this.actionLoadingId = '';
      }
    },
    async reject(pkg) {
      const confirm = await Swal.fire({
        icon: 'warning',
        title: 'Rechazar paquete',
        text: '¿Deseas rechazar esta solicitud?',
        showCancelButton: true,
        confirmButtonText: 'Rechazar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#dc3545',
      });
      if (!confirm.isConfirmed) return;
      this.actionLoadingId = pkg.id;
      try {
        await packagesApi.reject(pkg.id);
        await Swal.fire({ icon: 'success', title: 'Solicitud rechazada', confirmButtonColor: '#0d6efd' });
        await this.loadPendingPackages();
        await this.loadDecidedPackages();
      } catch (e) {
        await Swal.fire({ icon: 'error', title: 'Error', text: e.response?.data?.message || 'No se pudo rechazar.' });
      } finally {
        this.actionLoadingId = '';
      }
    },
    async cancelTrip() {
      const confirm = await Swal.fire({
        icon: 'warning',
        title: '¿Cancelar viaje?',
        text: 'El viaje quedará marcado como cancelado.',
        showCancelButton: true,
        confirmButtonText: 'Sí, cancelar',
      });
      if (!confirm.isConfirmed) return;
      try {
        await tripsApi.cancel(this.trip.id);
        await Swal.fire({ icon: 'success', title: 'Viaje cancelado' });
        await this.loadTrip();
      } catch (e) {
        await Swal.fire({
          icon: 'error',
          title: 'No se pudo cancelar',
          text: e.response?.data?.message || e.message || 'Error al cancelar viaje.',
        });
      }
    },
  },
};
</script>

<style scoped></style>
